import { Switch } from '@thanx/uikit/switch'
import { useBrameIntegration } from 'api/integrationsConfig'
import { ContentBlockTypes } from 'api/landingPageTheme'
import Spinner from 'components/Spinner'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import Blocks from 'scenes/Cms/components/Blocks'
import CmsPage from 'scenes/Cms/components/CmsPage'
import { AppLandingPageTheme } from 'slices/appLandingPageTheme/types'
import { ContentBlockData } from 'slices/appLandingPageTheme/types'
import AddNewBlockModal from './AddNewBlockModal'
import BlockItem from './BlockItem'
import EditBlock from './EditBlock'
import EmptyState from './EmptyState'
import ExpiredBlocks from './ExpiredBlocks'

const t = buildTranslate('cms.content.app_home_page.content')

type Props = {
  sidebar: React.ReactNode
  footer: React.ReactNode
  isFetching: boolean
  blockOrder: Array<string>
  setBlockOrder: (v: Array<string>) => void
  onNewBlock: (type: ContentBlockTypes) => void
  activeContentBlocks: AppLandingPageTheme['contentBlocks']
  allContentBlocks: AppLandingPageTheme['contentBlocks']
  expiredBlocks: AppLandingPageTheme['contentBlocks']
  updateContentBlock: (id: string, data: ContentBlockData) => void
  deleteContentBlock: (id: string) => void
  isEditModalOpen: boolean
  setIsEditModalOpen: (v: boolean) => void
  selectedBlockId: string | null
  setSelectedBlockId: (v: string | null) => void
  setIsFormValid: (v: boolean) => void
}

const ContentPage = ({
  sidebar,
  footer,
  isFetching,
  blockOrder,
  setBlockOrder,
  onNewBlock,
  activeContentBlocks,
  allContentBlocks,
  expiredBlocks,
  updateContentBlock,
  deleteContentBlock,
  isEditModalOpen,
  setIsEditModalOpen,
  selectedBlockId,
  setSelectedBlockId,
  setIsFormValid,
}: Props) => {
  const [isModalOpen, setModalOpen] = useState(false)

  const { brameIntegrationEnabled } = useBrameIntegration()

  const gamificationContentBlocksEnabled = useFlag(
    'gamification-content-blocks-enabled',
    false
  )

  // Game content blocks are only available when both the feature flag AND brame integration are enabled
  const gameContentBlocksAvailable =
    gamificationContentBlocksEnabled && brameIntegrationEnabled

  // Use the keys from activeContentBlocks and preserve the original order from blockOrder
  const activeBlockIds = Object.keys(activeContentBlocks)
  const filteredBlockOrder = blockOrder.filter(blockId => {
    if (!activeBlockIds.includes(blockId)) {
      return false
    }

    if (
      !gamificationContentBlocksEnabled &&
      activeContentBlocks[blockId]?.type === 'game'
    ) {
      return false
    }

    return true
  })

  const handleNewBlock = (type: ContentBlockTypes) => {
    onNewBlock(type)
    setModalOpen(false)
  }

  const handleEdit = (blockId: string) => {
    setSelectedBlockId(blockId)
    setIsEditModalOpen(true)
  }

  const handleDelete = (blockId: string) => {
    setBlockOrder(blockOrder.filter(b => b !== blockId))
    setIsEditModalOpen(false)
  }

  const handleClose = (previousState: ContentBlockData) => {
    selectedBlockId && updateContentBlock(selectedBlockId, previousState)
    setIsEditModalOpen(false)
    setIsFormValid(true)
  }

  const handleRemove = (blockId: string) => {
    setBlockOrder(blockOrder.filter(b => b !== blockId))
    deleteContentBlock(blockId)
    if (selectedBlockId === blockId) {
      setIsEditModalOpen(false)
    }
  }

  const handleReorder = (newFilteredOrder: Array<string>) => {
    if (gameContentBlocksAvailable) {
      setBlockOrder(newFilteredOrder)
      return
    }

    // When game content blocks are unavailable, merge filtered-out game blocks back
    // at their original relative positions to prevent data loss
    const gameBlocks = blockOrder.filter(
      id => activeContentBlocks[id]?.type === 'game'
    )

    if (gameBlocks.length === 0) {
      setBlockOrder(newFilteredOrder)
      return
    }

    // Build merged order in one pass over the original blockOrder
    const mergedOrder: string[] = []
    let filteredPointer = 0

    blockOrder.forEach(id => {
      const isGameBlock = activeContentBlocks[id]?.type === 'game'

      // If it's a game block, keep it at its original position
      if (isGameBlock) {
        mergedOrder.push(id)
      } else {
        // Check bounds before consuming from newFilteredOrder
        if (filteredPointer < newFilteredOrder.length) {
          mergedOrder.push(newFilteredOrder[filteredPointer])
          filteredPointer++
        } else {
          // Fallback to original id if we've exhausted the filtered array
          // eslint-disable-next-line no-console
          console.warn(
            `Merge logic exhausted filtered array, falling back to original id: ${id}`
          )
          mergedOrder.push(id)
        }
      }
    })

    setBlockOrder(mergedOrder)
  }

  if (isEditModalOpen && selectedBlockId) {
    // Check both active and expired blocks for the selected block
    const contentBlock =
      activeContentBlocks[selectedBlockId] || expiredBlocks[selectedBlockId]
    if (!contentBlock) {
      return null
    }
    const position = blockOrder.indexOf(selectedBlockId) + 1
    return (
      <EditBlock
        id={selectedBlockId}
        position={position}
        contentBlock={contentBlock}
        onDelete={handleDelete}
        onClose={handleClose}
        isLoading={isFetching}
        updateContentBlock={updateContentBlock}
      />
    )
  }

  return (
    <CmsPage
      className="mr-m"
      title={t('title')}
      description={t('description')}
      sidebar={sidebar}
    >
      <Spinner
        isLoading={isFetching}
        size="3x"
        className={'d-flex justify-content-center'}
      >
        <Switch condition={filteredBlockOrder.length === 0}>
          <EmptyState />
        </Switch>
        <Blocks
          type={'block'}
          order={filteredBlockOrder}
          setOrder={handleReorder}
          renderItem={(itemId, index) => {
            const blockData = activeContentBlocks[itemId]
            // Safety check: if block data is missing, don't render
            if (!blockData) {
              return null
            }
            return (
              <div data-block-id={itemId}>
                <BlockItem
                  id={itemId}
                  index={index}
                  data={blockData}
                  onEdit={handleEdit}
                />
              </div>
            )
          }}
          onNewBlock={() => setModalOpen(true)}
          expiredBlocks={
            <ExpiredBlocks
              expiredBlocks={expiredBlocks}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          }
        />
      </Spinner>

      {footer}

      <AddNewBlockModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onAddNewBlock={handleNewBlock}
        contentBlocks={allContentBlocks}
        blockOrder={blockOrder}
      />
    </CmsPage>
  )
}

export default ContentPage
